import React, { useEffect, useMemo, useRef, useState } from "react";
import { DeskRowMenu } from "../../../desktop/DeskTable";
import { formatLocationLabel, workTypeLabel } from "../../../../helpers/estimates";
import { formatCurrency } from "../../../../helpers/estimateTotals";

/** Single-line inline text entry: Enter saves, Esc cancels, blur cancels. */
function InlineName({ initial = "", placeholder, label, onSubmit, onCancel }) {
  const [value, setValue] = useState(initial);
  const [error, setError] = useState("");
  const busy = useRef(false);
  async function submit() {
    if (busy.current) return;
    const trimmed = value.trim();
    if (!trimmed) {
      setError("Enter a name.");
      return;
    }
    busy.current = true;
    try {
      await onSubmit(trimmed);
    } catch (err) {
      setError(err?.message || "Unable to save.");
      busy.current = false;
    }
  }
  return (
    <div className="jt-inline">
      <input
        className="eg-input"
        aria-label={label}
        placeholder={placeholder}
        autoFocus
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
          setError("");
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            submit();
          } else if (event.key === "Escape") {
            event.preventDefault();
            onCancel();
          }
        }}
        onBlur={() => {
          if (!busy.current) onCancel();
        }}
      />
      {error ? <div className="jt-inline__error" role="alert">{error}</div> : null}
    </div>
  );
}

/** Inline "+ Feature": type to filter catalog types / saved templates, or create a custom feature. */
function FeatureAdder({ featureTypes, templates, onAdd, onCancel }) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [error, setError] = useState("");
  const busy = useRef(false);
  const rootRef = useRef(null);
  useEffect(() => {
    rootRef.current?.scrollIntoView?.({ block: "nearest" });
  }, []);
  const q = query.trim().toLowerCase();

  const options = useMemo(() => {
    const catalog = (featureTypes || [])
      .filter((ft) => !q || ft.name?.toLowerCase().includes(q))
      .slice(0, 8)
      .map((ft) => ({ key: `c-${ft.feature_type_id}`, kind: "catalog", id: ft.feature_type_id, label: ft.name, hint: "Catalog" }));
    const tpl = (templates || [])
      .filter((t) => !q || t.template_name?.toLowerCase().includes(q))
      .slice(0, 5)
      .map((t) => ({ key: `t-${t.work_type_template_id}`, kind: "template", id: t.work_type_template_id, label: t.template_name, hint: "Template" }));
    const custom = query.trim()
      ? [{ key: "custom", kind: "custom", name: query.trim(), label: `Custom feature “${query.trim()}”`, hint: "New" }]
      : [];
    return [...catalog, ...tpl, ...custom];
  }, [featureTypes, templates, q, query]);

  // Default to the first real match; fall back to the custom entry.
  const activeIndex = Math.min(active, Math.max(options.length - 1, 0));

  async function choose(option) {
    if (!option || busy.current) return;
    busy.current = true;
    try {
      await onAdd(option);
    } catch (err) {
      setError(err?.message || "Unable to add feature.");
      busy.current = false;
    }
  }

  return (
    <div
      className="jt-inline jt-adder"
      ref={rootRef}
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget) || busy.current) return;
        const typed = query.trim();
        if (!typed) {
          onCancel();
          return;
        }
        choose(options[activeIndex] || { kind: "custom", name: typed });
      }}
    >
      <input
        className="eg-input"
        role="combobox"
        aria-expanded="true"
        aria-label="New feature"
        placeholder="Feature type or custom name…"
        autoFocus
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setActive(0);
          setError("");
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setActive(Math.min(activeIndex + 1, options.length - 1));
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActive(Math.max(activeIndex - 1, 0));
          } else if (event.key === "Enter") {
            event.preventDefault();
            choose(options[activeIndex]);
          } else if (event.key === "Escape") {
            event.preventDefault();
            onCancel();
          }
        }}
      />
      <ul className="jt-adder__list" role="listbox" aria-label="Feature options">
        {options.length === 0 ? <li className="jt-adder__empty">Type a name to create a custom feature.</li> : null}
        {options.map((option, index) => (
          <li
            key={option.key}
            role="option"
            aria-selected={index === activeIndex}
            className={index === activeIndex ? "is-active" : ""}
            onMouseDown={(event) => {
              event.preventDefault();
              choose(option);
            }}
          >
            <span>{option.label}</span>
            <span className="jt-adder__hint">{option.hint}</span>
          </li>
        ))}
      </ul>
      {error ? <div className="jt-inline__error" role="alert">{error}</div> : null}
    </div>
  );
}

function RowMenu({ label, onRename, onDelete, canDelete = true }) {
  const items = [
    { key: "rename", label: "Rename" },
    canDelete && { key: "delete", label: "Delete…", danger: true },
  ].filter(Boolean);
  return (
    <DeskRowMenu
      label={label}
      items={items}
      onSelect={(item) => (item.key === "rename" ? onRename() : onDelete())}
    />
  );
}

function ConfirmDelete({ what, onConfirm, onCancel }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <div className="jt-confirm" role="alertdialog" aria-label={`Delete ${what}`}>
      <span>Delete “{what}”?</span>
      <span className="jt-confirm__actions">
        <button
          type="button"
          className="eg-link is-danger"
          disabled={busy}
          autoFocus
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm();
            } catch (err) {
              setError(err?.message || "Unable to delete.");
              setBusy(false);
            }
          }}
        >
          {busy ? "Deleting…" : "Delete"}
        </button>
        <button type="button" className="eg-link" onClick={onCancel}>Cancel</button>
      </span>
      {error ? <span className="jt-inline__error" role="alert">{error}</span> : null}
    </div>
  );
}

/**
 * Left panel: locations with nested features and their totals.
 * Purely presentational — every write is an injected handler.
 *
 * totalsByLocation: { [roomId]: number }, totalsByFeature: { [featureId]: number }
 */
export function JobTree({
  locations,
  featureTypes,
  templates,
  selectedFeatureId,
  totalsByLocation,
  totalsByFeature,
  editable,
  onSelectFeature,
  onAddLocation,
  onRenameLocation,
  onDeleteLocation,
  onAddFeature,
  onRenameFeature,
  onDeleteFeature,
}) {
  const [addingLocation, setAddingLocation] = useState(false);
  const [addingFeatureIn, setAddingFeatureIn] = useState(null);
  const [renaming, setRenaming] = useState(null); // `loc:<id>` | `feat:<id>`
  const [deleting, setDeleting] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  return (
    <nav className="jt" aria-label="Job tree">
      <div className="jt__head">
        <span className="jt__title">Job</span>
        {editable ? (
          <button type="button" className="eg-link" onClick={() => setAddingLocation(true)}>
            + Location
          </button>
        ) : null}
      </div>

      {addingLocation ? (
        <InlineName
          label="New location name"
          placeholder="Location name, e.g. Master Bathroom"
          onCancel={() => setAddingLocation(false)}
          onSubmit={async (name) => {
            await onAddLocation(name);
            setAddingLocation(false);
          }}
        />
      ) : null}

      {locations.length === 0 && !addingLocation ? (
        <p className="jt__empty">No locations yet. Use “+ Location” to start.</p>
      ) : null}

      <ul className="jt__list">
        {locations.map((location) => {
          const roomId = location.room_id;
          const name = formatLocationLabel(location);
          const isCollapsed = collapsed[roomId];
          return (
            <li key={roomId} className="jt__loc">
              {renaming === `loc:${roomId}` ? (
                <InlineName
                  initial={location.name || ""}
                  label="Location name"
                  onCancel={() => setRenaming(null)}
                  onSubmit={async (next) => {
                    await onRenameLocation(location, next);
                    setRenaming(null);
                  }}
                />
              ) : deleting === `loc:${roomId}` ? (
                <ConfirmDelete
                  what={name}
                  onCancel={() => setDeleting(null)}
                  onConfirm={async () => {
                    await onDeleteLocation(location);
                    setDeleting(null);
                  }}
                />
              ) : (
                <div className="jt__row jt__row--loc">
                  <button
                    type="button"
                    className="jt__toggle"
                    aria-expanded={!isCollapsed}
                    aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${name}`}
                    onClick={() => setCollapsed((c) => ({ ...c, [roomId]: !c[roomId] }))}
                  >
                    {isCollapsed ? "▸" : "▾"}
                  </button>
                  <span className="jt__name" title={name}>{name}</span>
                  <span className="jt__amt">{formatCurrency(totalsByLocation[roomId] || 0)}</span>
                  {editable ? (
                    <RowMenu
                      label={`${name} actions`}
                      canDelete={!location.is_default_scope}
                      onRename={() => setRenaming(`loc:${roomId}`)}
                      onDelete={() => setDeleting(`loc:${roomId}`)}
                    />
                  ) : null}
                </div>
              )}

              {!isCollapsed ? (
                <ul className="jt__features">
                  {(location.workTypes || []).map((workType) => {
                    const featureId = workType.estimate_feature_id;
                    const label = workTypeLabel(workType);
                    if (renaming === `feat:${featureId}`) {
                      return (
                        <li key={featureId}>
                          <InlineName
                            initial={label}
                            label="Feature name"
                            onCancel={() => setRenaming(null)}
                            onSubmit={async (next) => {
                              await onRenameFeature(workType, next);
                              setRenaming(null);
                            }}
                          />
                        </li>
                      );
                    }
                    if (deleting === `feat:${featureId}`) {
                      return (
                        <li key={featureId}>
                          <ConfirmDelete
                            what={label}
                            onCancel={() => setDeleting(null)}
                            onConfirm={async () => {
                              await onDeleteFeature(workType, location);
                              setDeleting(null);
                            }}
                          />
                        </li>
                      );
                    }
                    const selected = featureId === selectedFeatureId;
                    return (
                      <li key={featureId} className={`jt__row jt__row--feat${selected ? " is-selected" : ""}`}>
                        <button
                          type="button"
                          className="jt__feat-btn"
                          aria-current={selected ? "true" : undefined}
                          onClick={() => onSelectFeature(featureId, roomId)}
                        >
                          <span className="jt__name" title={label}>{label}</span>
                          <span className="jt__amt">{formatCurrency(totalsByFeature[featureId] || 0)}</span>
                        </button>
                        {editable ? (
                          <RowMenu
                            label={`${label} actions`}
                            onRename={() => setRenaming(`feat:${featureId}`)}
                            onDelete={() => setDeleting(`feat:${featureId}`)}
                          />
                        ) : null}
                      </li>
                    );
                  })}
                  {editable ? (
                    <li className="jt__add">
                      {addingFeatureIn === roomId ? (
                        <FeatureAdder
                          featureTypes={featureTypes}
                          templates={templates}
                          onCancel={() => setAddingFeatureIn(null)}
                          onAdd={async (option) => {
                            await onAddFeature(location, option);
                            setAddingFeatureIn(null);
                          }}
                        />
                      ) : (
                        <button type="button" className="eg-link" onClick={() => setAddingFeatureIn(roomId)}>
                          + Feature
                        </button>
                      )}
                    </li>
                  ) : null}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
